import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence, useScroll, useTransform, useSpring } from "framer-motion";
import { Phone, Sparkles } from "lucide-react";
import { BRAND } from "@/data/content";
import HammersBackdrop from "@/components/site/HammersBackdrop";

const BACKDROP_PHOTOS = [
  "https://customer-assets.emergentagent.com/job_craft-solutions-new/artifacts/ogdv4p8y_695762166_1456990872892832_7219909194621199734_n.jpg",
  "https://customer-assets.emergentagent.com/job_craft-solutions-new/artifacts/knczu6ol_696501483_976575178070449_3976293815239858773_n.jpg",
  "https://customer-assets.emergentagent.com/job_craft-solutions-new/artifacts/js885s6l_665851214_1785095105787652_6055593490093181374_n.jpg",
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollY } = useScroll();
  // Hammer rotates as you scroll the page up/down (parallax + spin)
  const rotateRaw = useTransform(scrollY, [0, 1200], [-12, 360]);
  const rotate = useSpring(rotateRaw, { mass: 2, stiffness: 50, damping: 22 });
  const yShift = useTransform(scrollY, [0, 800], [0, -40]);
  const bgScale = useTransform(scrollY, [0, 600], [1, 1.08]);

  const [photoIndex, setPhotoIndex] = useState(0);
  useEffect(() => {
    const id = setInterval(() => {
      setPhotoIndex((i) => (i + 1) % BACKDROP_PHOTOS.length);
    }, 3000);
    return () => clearInterval(id);
  }, []);

  return (
    <section
      ref={ref}
      className="relative overflow-hidden bg-paper pt-8 md:pt-12 pb-20 md:pb-28"
      data-testid="hero-section"
    >
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#16a34a]/10 blur-3xl" />
        <div className="absolute top-40 -right-24 w-[380px] h-[380px] rounded-full bg-[#eab308]/15 blur-3xl" />
      </div>

      {/* Spinning hammer backdrop — rotates with page scroll, in sync with main logo */}
      <HammersBackdrop />

      <div className="max-w-6xl mx-auto px-5 md:px-8 lg:px-12 relative z-10">
        {/* Logo with cinematic photo backdrop */}
        <div className="flex items-center justify-center">
          <motion.div style={{ y: yShift }} className="relative isolate">
            {/* Themed photo backdrop — bigger frame, slideshow of 3 real project photos */}
            <div
              className="absolute -inset-20 sm:-inset-28 md:-inset-40 lg:-inset-48 z-0 overflow-hidden rounded-[40%] shadow-[0_50px_100px_-30px_rgba(0,0,0,0.55)] ring-1 ring-black/10 bg-[#0f1d12]"
              data-testid="hero-photo-backdrop"
            >
              <AnimatePresence mode="sync">
                <motion.img
                  key={photoIndex}
                  src={BACKDROP_PHOTOS[photoIndex]}
                  alt=""
                  aria-hidden="true"
                  initial={{ opacity: 0, scale: 1.08 }}
                  animate={{ opacity: 1, scale: 1.02 }}
                  exit={{ opacity: 0, scale: 1.04 }}
                  transition={{ duration: 1.2, ease: "easeInOut" }}
                  style={{ scale: bgScale }}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </AnimatePresence>

              {/* Subtle warm depth */}
              <div className="absolute inset-0 bg-gradient-to-t from-[#1c1917]/55 via-transparent to-[#1c1917]/15" />
              {/* Soft cream halo so the cartoon logo reads cleanly in the center */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(closest-side at 50% 50%, rgba(253,251,247,0.55) 0%, rgba(253,251,247,0.18) 38%, rgba(253,251,247,0) 64%)",
                }}
              />
              {/* Edge vignette to ground the frame */}
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  boxShadow:
                    "inset 0 0 160px 40px rgba(28,25,23,0.55), inset 0 0 0 1px rgba(255,255,255,0.06)",
                }}
              />
              {/* Golden ray streak */}
              <div
                className="absolute -top-10 left-1/4 w-[80%] h-44 opacity-60 mix-blend-screen pointer-events-none rotate-[18deg]"
                style={{
                  background:
                    "linear-gradient(90deg, rgba(255,255,255,0) 0%, rgba(253,224,71,0.45) 45%, rgba(255,255,255,0) 100%)",
                  filter: "blur(28px)",
                }}
              />
            </div>

            {/* Slideshow dots */}
            <div className="absolute -bottom-24 sm:-bottom-32 md:-bottom-44 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2">
              {BACKDROP_PHOTOS.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setPhotoIndex(i)}
                  aria-label={`Show backdrop photo ${i + 1}`}
                  data-testid={`hero-backdrop-dot-${i}`}
                  className={`h-1.5 rounded-full transition-all duration-300 ${
                    i === photoIndex
                      ? "w-8 bg-[#16a34a]"
                      : "w-2 bg-[#1c1917]/30 hover:bg-[#1c1917]/55"
                  }`}
                />
              ))}
            </div>

            {/* Sun-flare accents */}
            <div className="absolute -top-12 -right-12 w-44 h-44 rounded-full bg-[#eab308]/55 blur-3xl z-0 pointer-events-none" />
            <div className="absolute -bottom-12 -left-10 w-52 h-52 rounded-full bg-[#16a34a]/55 blur-3xl z-0 pointer-events-none" />

            <motion.div
              animate={{ y: [0, -14, 0] }}
              transition={{ duration: 4.2, repeat: Infinity, ease: "easeInOut" }}
              className="relative z-10"
            >
              <motion.img
                src={BRAND.logo}
                alt="ProCraft Solutions logo"
                style={{ rotate }}
                className="hammer-glow w-[280px] sm:w-[340px] md:w-[420px] lg:w-[460px] aspect-square object-contain rounded-3xl drop-shadow-[0_24px_36px_rgba(0,0,0,0.5)]"
                data-testid="hero-logo-animated"
              />
            </motion.div>

            {/* Floating chips */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="absolute -left-12 top-2 z-20 hidden md:flex items-center gap-2 px-4 h-11 rounded-full bg-white/95 backdrop-blur border border-[#e7e5df] shadow-md font-bold text-sm"
            >
              <span className="w-2.5 h-2.5 rounded-full bg-[#16a34a] animate-pulse" />
              Free Estimates
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="absolute -right-12 bottom-2 z-20 hidden md:flex items-center gap-2 px-4 h-11 rounded-full bg-[#eab308] text-[#1c1917] shadow-md font-bold text-sm"
            >
              Licensed · Insured · On Time
            </motion.div>
          </motion.div>
        </div>

        {/* Text + CTAs centered below the logo */}
        <div className="text-center mt-32 md:mt-44 lg:mt-52">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-white border border-[#e7e5df] text-xs font-bold uppercase tracking-[0.18em] text-[#16a34a]"
            data-testid="hero-eyebrow"
          >
            <Sparkles className="w-3.5 h-3.5" />
            Locally Owned · Tampa Bay
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: "easeOut" }}
            className="font-display font-black text-[#1c1917] mt-5 text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
            data-testid="hero-headline"
          >
            Build Smart.
            <br />
            <span className="text-[#16a34a]">From The</span>{" "}
            <span className="relative inline-block">
              Start.
              <span className="absolute -bottom-2 left-0 h-2 w-full bg-[#eab308] rounded-full -z-10" />
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15 }}
            className="mt-6 text-lg md:text-xl text-[#57534e] max-w-2xl mx-auto leading-relaxed"
            data-testid="hero-subheadline"
          >
            ProCraft Solutions is a locally owned company delivering reliable,
            high-quality property service you can trust. Landscaping, monthly
            lawn care, lighting, drainage, retaining walls, pavers, and
            handyman work — for residential and commercial properties.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.25 }}
            className="mt-8 flex justify-center"
          >
            <a
              href={BRAND.phoneHref}
              data-testid="hero-phone-cta"
              className="group inline-flex items-center justify-center gap-3 h-14 px-7 rounded-full bg-[#16a34a] hover:bg-[#15803d] text-white font-bold text-base transition-all duration-300 active:scale-[0.98] shadow-[0_10px_30px_-10px_rgba(22,163,74,0.6)]"
            >
              <Phone className="w-5 h-5 group-hover:rotate-12 transition" />
              Call {BRAND.phone}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="mt-8 flex items-center justify-center gap-6 text-sm text-[#57534e]"
            data-testid="hero-trust-badges"
          >
            <div className="flex items-center gap-2">
              <div className="flex">
                {[...Array(5)].map((_, i) => (
                  <svg key={i} className="w-4 h-4" viewBox="0 0 24 24" fill="#FBBC04">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <span className="font-semibold text-[#1c1917]">5.0</span>
              <span>on Google</span>
            </div>
            <div className="hidden sm:block w-px h-5 bg-[#e7e5df]" />
            <div className="hidden sm:block">
              Licensed · Insured · On Time
              <span className="block text-xs text-[#57534e] mt-0.5">
                Lic. #L23000070666
              </span>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
